import { derivePrivateKey, generateSessionKeyPair, sign, signatureToBase64 } from '../../../shared/lib/crypto';
import { PBKDF2_SALT, PBKDF2_ITERATIONS, DEFAULT_SESSION_EXPIRATION } from '../../../shared/lib/auth-config';
import { secureSessionStorage } from '../../../shared/lib/storage';
import { acceptLanguage } from '@/shared/lib/accept-language';
import {
  backendValidationErrorFrom,
  type BackendErrorResponse,
} from '../../../shared/lib/backend-errors';
import type { 
  SessionCreationRequest, 
  SessionCreationResponse, 
  BackendSessionResponse,
  AuthenticatedUser,
  SessionUser 
} from '../model/types';

export async function createSession(
  email: string, 
  password: string, 
  expiration: number = DEFAULT_SESSION_EXPIRATION
): Promise<SessionCreationResponse> {
  try {
    // 1. Derive the user's private key from credentials for initial authentication
    const userPrivateKey = await derivePrivateKey(email, password, PBKDF2_SALT, PBKDF2_ITERATIONS);

    // 2. Create authenticated user object for initial verification
    const authenticatedUser: AuthenticatedUser = {
      id: email,
      type: 'person',
      privateKey: userPrivateKey
    };

    // 3. Generate a new temporary key pair for the session
    const { privateKey: sessionPrivateKey, publicKey: sessionPublicKey } = generateSessionKeyPair();

    // 4. Convert public key to PEM format using starkbank-ecdsa's built-in method
    const publicKeyPem = sessionPublicKey.toPem();

    // 5. Create session request body
    const sessionRequest: SessionCreationRequest = {
      sessions: [
        {
          public_key: publicKeyPem, // Use proper PEM format from starkbank-ecdsa
          expiration: expiration
        }
      ]
    };

    // 6. Make POST request to /session endpoint
    
    // Generate timestamp once and use it for both signature and header
    const currentTime = (Date.now() / 1000).toString();
    const authSignature = await createInitialAuthSignature(authenticatedUser, sessionRequest, currentTime);
    
    const requestHeaders = {
      'Content-Type': 'application/json',
      // Include authentication headers for the initial request
      'User-Id': authenticatedUser.id,
      'User-Type': authenticatedUser.type,
      'Access-Time': currentTime, // Use the same timestamp as signature
      'Access-Signature': authSignature,
      'Accept-Language': acceptLanguage(),
    };
    
    // Always use /api proxy - handled by Vite in development and nginx in production
    const response = await fetch('/api/session', {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify(sessionRequest)
    });

    if (!response.ok) {
      let errorData: BackendErrorResponse;
      try {
        errorData = await response.json();
      } catch {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }
      const validationError = backendValidationErrorFrom(
        errorData,
        `HTTP ${response.status}: ${response.statusText}`,
      );
      return {
        success: false,
        error: validationError.message,
        validationError,
      };
    }

    const responseData = await response.json() as BackendSessionResponse;

    if (responseData.sessions && responseData.sessions.length > 0 && responseData.sessions[0].id) {
      // 7. Store the session private key securely
      const sessionId = responseData.sessions[0].id;
      const expiresAt = new Date(Date.now() + expiration * 1000).toISOString();
      await secureSessionStorage.storeSessionKey(
        sessionId,
        sessionPrivateKey,
        expiresAt,
        email
      );

      return {
        success: true,
        data: {
          session_id: sessionId,
          expires_at: expiresAt
        }
      };
    } else {
      return {
        success: false,
        error: 'Session creation failed - no session ID returned'
      };
    }
  } catch (error) {
    console.error('❌ Session creation error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown session creation error'
    };
  }
}

async function createInitialAuthSignature(
  user: AuthenticatedUser, 
  body: SessionCreationRequest,
  currentTime: string
): Promise<string> {
  const message = `${user.id}:${currentTime}:${JSON.stringify(body)}`;
  const signature = sign(user.privateKey, message);
  
  // Convert signature to base64 format
  return signatureToBase64(signature);
}

export async function getSessionUser(sessionId: string): Promise<SessionUser | null> {
  try {
    const sessionPrivateKey = await secureSessionStorage.getSessionKey(sessionId);
    
    if (!sessionPrivateKey) {
      return null;
    }

    // Extract user info from session storage (we stored it with the key)
    // For now, we'll need to modify the storage to also return user info
    // This is a simplified implementation
    return {
      id: 'tech@veltiz.com', // This should be retrieved from storage
      type: 'person',
      sessionId,
      sessionPrivateKey,
      expiresAt: new Date(Date.now() + DEFAULT_SESSION_EXPIRATION * 1000).toISOString()
    };
  } catch (error) {
    console.error('❌ Failed to get session user:', error);
    return null;
  }
}
